import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Tooltip from '@mui/joy/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {formatDateTime} from '../../../common/dateTime';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Stack from "@mui/joy/Stack";
import IconButton, {iconButtonClasses} from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Dropdown from '@mui/joy/Dropdown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockIcon from '@mui/icons-material/Block';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import FastForwardIcon from '@mui/icons-material/FastForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StopIcon from '@mui/icons-material/Stop';
import {Bot, BotMode, BotModeDescription, BotModeName, BotsListRequest, BotState} from "../../../models/bot";
import {Proxy, ProxyDestination} from "../../../models/proxy";
import {NotificationService} from "../../../common/notification";
import ConfirmDialog from "../../../components/confirmDialog/ConfirmDialog";
import {
    botsListApi,
    botsListStatesApi,
    botsRestartBotApi,
    botsStartBotApi,
    botsStopBotApi,
    deleteBotApi
} from "../botsApi";
import {proxiesSearchApi} from "../../proxies/proxiesApi";
import {useNavigate} from "react-router-dom";
import Link from '@mui/joy/Link';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListDivider from '@mui/joy/ListDivider';
import DebounceInput from '../../../components/debounceInput/DebounceInput';
import AddIcon from "@mui/icons-material/Add";
import {getPages} from "../../../common/pages/pages";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {botsStartAllBotApi} from "../botsApi";
import {convertToBoolean, convertFromBoolean} from "../../../common/utils";

const ALL_RUNTIME_STATES = ['stopped', 'running', 'starting'] as const;

export default function OrderTable() {
    const navigate = useNavigate();
    const [bots, setBots] = React.useState<Bot[]>([]);
    const [botStates, setBotStates] = React.useState<BotState[]>([]);
    const [proxies, setProxies] = React.useState<Proxy[]>([]);
    const [pageCount, setPageCount] = React.useState(1);
    const [open, setOpen] = React.useState(false);
    const [filterRequest, setFilterRequest] = React.useState<BotsListRequest>({
        page: 1,
        count: 150,
        // По умолчанию показываем только активные боты.
        isDisabled: false
    });
    const [stateFilter, setStateFilter] = React.useState<string[]>([...ALL_RUNTIME_STATES]);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [statesLoaded, setStatesLoaded] = React.useState(false);
    const [backdrop, setBackdrop] = React.useState(false);
    const id = React.useRef<number>(0);
    const STATES_LOAD_MS = 15000;

    React.useEffect(() => {
        loadProxy().catch(console.error);
    }, [])

    React.useEffect(() => {
        if (!open) {
            loadData().catch(console.error);
        }
    }, [filterRequest, open])

    const loadStates = async () => {
        let result = await botsListStatesApi(bots.map(e => e.id));
        if (result.result) {
            const res = result.value!;
            setBotStates(res);
            setStatesLoaded(true);
        } else {
            console.log("Ошибка загрузки статусов ботов")
        }
    }

    React.useEffect(() => {
        if (bots.length === 0) {
            return;
        }

        // Сразу помле получения коллекции ботов пробуем получить статусы
        loadStates().catch(console.error);

        // А потом обновялем каждые 15 секунд
        const interval = setInterval(async () => {
            if (bots.length > 0) {
                await loadStates().catch(console.error);
            }
        }, STATES_LOAD_MS);

        return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }, [bots])

    const renderFilters = () => {
        return (
            <React.Fragment>
                <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Активность"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={convertFromBoolean(filterRequest.isDisabled, 1, 0)}
                        onChange={(_, value) => setFilterRequest(prevRequest => ({
                            ...prevRequest,
                            // Важно: для значения `0` оператор `||` подменяет на `-1`.
                            // Нам нужно отличать `0` (Активный) от `null/undefined` (Все).
                            isDisabled: convertToBoolean(value ?? -1, 1, 0)
                        }))}
                    >
                        <Option value={-1}>Все</Option>
                        <Option value={0}>Активный</Option>
                        <Option value={1}>Выключен</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Состояние</FormLabel>
                    <Select size="sm" multiple placeholder="Все"
                            value={stateFilter}
                            onChange={(_, value) => setStateFilter(value as string[])}>
                        <Option value="stopped">Остановлен</Option>
                        <Option value="running">Запущен</Option>
                        <Option value="starting">Запускается</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Режим работы</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Режим работы"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={filterRequest.mode?.length === 1 ? filterRequest.mode[0] : -1}
                        onChange={(_, value) => setFilterRequest(prevRequest => ({
                                ...prevRequest,
                                mode: (value ?? 0) > -1 ? [value as BotMode] : undefined
                            }))}
                    >
                        <Option value={-1}>Все</Option>
                        {
                            Object.entries(BotModeName).map((entity) => <Option key={Number(entity[0])}
                                value={Number(entity[0])}>{entity[1]}</Option>)
                        }
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Прокси</FormLabel>
                    <Select size="sm"
                            placeholder="Прокси"
                            value={filterRequest.proxy}
                            onChange={(_, value) => setFilterRequest(prevRequest => ({
                                ...prevRequest,
                                proxy: (value ?? 0) > 0 ? Number(value) : undefined
                            }))}
                    >
                        <Option key={-1} value={-1}>Все</Option>
                        {proxies.map((p) =>
                            <Option key={p.id} value={p.id}>{p.proxyHost}:{p.proxyPort}</Option>
                        )}
                    </Select>
                </FormControl>
            </React.Fragment>
        );
    }

    const applyFilter = async () => {
        setOpen(false)
        if (filterRequest.page !== 1) {
            setFilterRequest(prevRequest => ({
                ...prevRequest,
                page: 1
            }));
        }
    }

    const loadProxy = async () => {
        //Беру заведомо большое число зная что прокси общего типа не будет много
        let result = await proxiesSearchApi({page:1,count:1000,destination:ProxyDestination.BotsOnly});
        if (result.result) {
            setProxies(result.value!.items)
        } else {
            NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
        }
    }

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await botsListApi(filterRequest);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.count / filterRequest.count));
                setBots(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const remove = (value: number) => {
        id.current = value;
        setConfirmOpen(true)
    }

    const stop = async (id: number) => {
        setLoading(true)
        try {
            let result = await botsStopBotApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Команда остановки бота успешно отправлена.")
            } else {
                NotificationService.alertError("Не удалось отправить команду остановки бота. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }
    }

    const start = async (id: number) => {
        setLoading(true)
        try {
            let result = await botsStartBotApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Команда запуска бота успешно отправлена.")
            } else {
                NotificationService.alertError("Не удалось отправить команду запуска бота. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }
    }

    const restart = async (id: number) => {
        setLoading(true)
        try {
            let result = await botsRestartBotApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Команда перезапуска бота успешно отправлена.")
            } else {
                NotificationService.alertError("Не удалось отправить команду перезапуска бота. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }
    }

    const onDelete = async () => {
        setLoading(true)
        try {
            const res = await deleteBotApi(id.current)
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            setLoading(false)
            id.current = 0;
        }
    }

    const edit = (id: number) => {
        window.open(`/app/bots/${id}`, '_blank', 'noopener,noreferrer');
    }

    const getBotState = (id?: number): BotState | undefined => {
        if (!id)
            return undefined;
        const st = botStates.filter(p => p.id === id);
        if (st.length !== 1)
            return undefined;
        const state = st[0];
        return state
    }

    const getStateIcon = (state: BotState): any => 
        !state.isStarted
            ? <StopIcon/>
            : state.isRunning ? <CheckRoundedIcon/> : <HourglassBottomIcon/>;

    const getStateColor = (state: BotState): "danger" | "success" | "warning" => 
        !state.isStarted
            ? 'danger'
            : state.isRunning ? 'success' : 'warning';

    const getStateDescription = (state: BotState): string =>
        !state.isStarted
            ? `Остановлен`
            : state.isRunning ? 'Запущен' : 'Запускается';

    // Подсказка о загрузке инвентаря ботом; не показывается, если бот инвентарь не загружает и ограничения нет
    const renderInventoryLoadHint = (state: BotState) => {
        const hasCount = state.inventoryLoadRequestsCount != null;
        const hasLimit = state.inventoryLoadTooManyRequestsUntil != null;
        if (!hasCount && !hasLimit) {
            return null;
        }
        return (
            <Tooltip
                variant="outlined"
                title={
                    <Stack spacing={0.5}>
                        {hasCount &&
                            <Typography level="body-xs">Запросов на загрузку инвентаря: {state.inventoryLoadRequestsCount}</Typography>}
                        {hasLimit &&
                            <Typography level="body-xs">Не выбирается для загрузки инвентаря (429) до: {formatDateTime(state.inventoryLoadTooManyRequestsUntil)}</Typography>}
                    </Stack>
                }
            >
                <InfoOutlinedIcon sx={{fontSize: 16, cursor: 'help', color: hasLimit ? 'warning.500' : 'neutral.500'}}/>
            </Tooltip>
        );
    };

    const getRuntimeStateKey = (state?: BotState) =>
        !state ? undefined
            : !state.isStarted ? 'stopped'
            : state.isRunning ? 'running' : 'starting';

    const visibleBots = bots.filter(bot => {
        if (stateFilter.length === ALL_RUNTIME_STATES.length) return true;
        const key = getRuntimeStateKey(getBotState(bot.id));
        return key !== undefined && stateFilter.includes(key);
    });

    const startAllBots = async () => {
        setBackdrop(true)
        try {
            let result = await botsStartAllBotApi();
            if (result.result) {
                NotificationService.alertSuccess("Команда запуска успешно отправлена.")
            } else {
                NotificationService.alertError("Не удалось отправить команду запуска. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setBackdrop(false)
        }
    };

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                gap: 1,
            }}
        >
            <Backdrop
                sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
                open={backdrop}
                onClick={() => setBackdrop(false)}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: { xs: 'row', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Chip variant="outlined"
                      color="success"
                      size="sm"
                      startDecorator={<FastForwardIcon fontSize='medium' />}
                      onClick={startAllBots}>
                    Запустить все активные
                </Chip>
            </Box>

            <Sheet
                className="SearchAndFilters-mobile"
                sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
            >
                <DebounceInput
                    size="sm"
                    placeholder="Поиск"
                    startDecorator={<SearchIcon/>}
                    sx={{flexGrow: 1}}
                    debounceTimeout={1000}
                    handleDebounce={(val) => setFilterRequest(prevRequest => ({
                        ...prevRequest,
                        search: val || undefined
                    }))}
                />
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => setOpen(true)}
                >
                    <FilterAltIcon/>
                </IconButton>
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => navigate("0")}
                >
                    <AddIcon/>
                </IconButton>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                        <ModalClose/>
                        <Typography id="filter-modal" level="h2">
                            Фильтры
                        </Typography>
                        <Divider sx={{my: 2}}/>
                        <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            {renderFilters()}
                            <Button color="primary" onClick={applyFilter}>
                                Применить
                            </Button>
                            <Button color="neutral" onClick={() => setOpen(false)}>
                                Закрыть
                            </Button>
                        </Sheet>
                    </ModalDialog>
                </Modal>
            </Sheet>

            <ConfirmDialog
                title="Удалить?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDelete}
            >
                Вы уверены что хотите удалить бота?
            </ConfirmDialog>

            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Поиск ботов</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Поиск"
                        startDecorator={<SearchIcon/>}
                        debounceTimeout={1000}
                        handleDebounce={(val) => setFilterRequest(prevRequest => ({
                            ...prevRequest,
                            search: val || undefined
                        }))}
                    />
                </FormControl>
                {renderFilters()}
                <FormControl size="sm" sx={{
                    justifyContent: 'end'
                }}>
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => navigate("0")}
                    >
                        <AddIcon/>
                    </IconButton>
                </FormControl>
            </Box>
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 100, padding: '12px 6px'}}>Активный</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Id</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Steam Id</th>
                                <th style={{width: 240, padding: '12px 6px'}}>Имя (никнейм)</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Прокси</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Режим работы</th>
                                <th style={{width: 100, padding: '12px 6px'}}>Статус</th>
                                <th style={{width: 50, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {visibleBots.map((row) => {
                                const state = getBotState(row.id);
                                return(
                                <tr key={row.id.toString()} style={row.bannedAt ? {backgroundColor: 'red'} : {}}>
                                    <td>
                                        <Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={row.isDisabled ? <BlockIcon/> : <CheckRoundedIcon/>}
                                            color={row.isDisabled ? 'danger' : 'success'}
                                        >
                                            {row.isDisabled ? "Выключен" : "Активен"}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Link
                                            level="body-sm"
                                            href={`/app/bots/${row.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {row.id.toString()}
                                        </Link>
                                    </td>
                                    <td>
                                        <Stack spacing={.5} direction={'column'}>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${row.steamId}/`}
                                                target="_blank"
                                                level="body-xs"
                                            >
                                                {row.steamId.toString()}
                                            </Link>
                                            <Typography level="body-xs" textColor={'background.backdrop'}>(level #{row.level})</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Stack spacing={.5} direction={'column'}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {row.avatar && (
                                                    <Box component="img" src={row.avatar} sx={{ height: "20px", width: "20px", borderRadius: '50%' }} />
                                                )}
                                                <Typography level="body-xs">
                                                    {row.nickName ? `${row.userName} (${row.nickName})` : row.userName}
                                                </Typography>
                                            </Stack>
                                            <Typography level="body-xs" textColor={'background.backdrop'}>{row.comment}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.proxyAddress}</Typography>
                                    </td>
                                    <td>
                                        <Tooltip title={BotModeDescription[row.mode]}>
                                            <Typography level="body-xs">{BotModeName[row.mode]}</Typography>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        {!state
                                        ?<>-</>
                                        :<Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title={state.stopReason}>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={getStateIcon(state)}
                                                    color={getStateColor(state)}
                                                >
                                                    {getStateDescription(state)}
                                                </Chip>
                                            </Tooltip>
                                            {renderInventoryLoadHint(state)}
                                        </Stack>}
                                    </td>
                                    <td>
                                        <Dropdown>
                                            <MenuButton
                                                slots={{root: IconButton}}
                                                slotProps={{
                                                    root: {
                                                        variant: 'plain',
                                                        color: 'neutral',
                                                        size: 'sm'
                                                    }
                                                }}
                                            >
                                                <MoreHorizRoundedIcon/>
                                            </MenuButton>
                                            <Menu size="sm" sx={{minWidth: 140}}>
                                                <MenuItem onClick={() => edit(row.id)}>Редактировать</MenuItem>
                                                <Divider/>
                                                <MenuItem color="danger"
                                                            onClick={() => remove(row.id)}>Удалить</MenuItem>
                                                {!statesLoaded
                                                ?<></>
                                                :<>
                                                    <Divider/>
                                                    {state?.isStarted
                                                    ?<>
                                                        <MenuItem color="danger" onClick={() => stop(row.id)}>
                                                            <ListItemIcon sx={{ minWidth: 0 }}><StopIcon fontSize="small" /></ListItemIcon>
                                                            <ListItemText primary="Остановить"
                                                                            primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                        </MenuItem>
                                                        <Divider/>
                                                        <MenuItem color="warning" onClick={() => restart(row.id)}>
                                                            <ListItemIcon sx={{ minWidth: 0 }}><RestartAltIcon fontSize="small" /></ListItemIcon>
                                                            <ListItemText primary="Перезапустить"
                                                                            primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                        </MenuItem>
                                                    </>
                                                    : <MenuItem color="success" onClick={() => start(row.id)}>
                                                        <ListItemIcon sx={{ minWidth: 0 }}><PlayArrowIcon fontSize="small" /></ListItemIcon>
                                                        <ListItemText primary="Запустить"
                                                                        primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                    </MenuItem>}
                                                </>}
                                            </Menu>
                                        </Dropdown>
                                    </td>
                                </tr>)
                            })}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={filterRequest.page === 1}
                            onClick={() => setFilterRequest(prevRequest => ({
                                ...prevRequest,
                                page: prevRequest.page - 1
                            }))}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={filterRequest.page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setFilterRequest(prevRequest => ({
                                    ...prevRequest,
                                    page: Number(p)
                                }))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={filterRequest.page === pageCount}
                            onClick={() => setFilterRequest(prevRequest => ({
                                ...prevRequest,
                                page: prevRequest.page + 1
                            }))}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {visibleBots.map((listItem) => {
                            const state = getBotState(listItem.id);
                            return(
                            <List key={listItem.id.toString()} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                    style={listItem.bannedAt ? {backgroundColor: 'red'} : {}}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Link
                                                level="body-sm"
                                                href={`/app/bots/${listItem.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <Typography gutterBottom sx={{fontWeight: 600}}>
                                                    {listItem.steamId.toString()} (#{listItem.id.toString()})
                                                </Typography>
                                            </Link>
                                            <Typography level="body-xs">Level #{listItem.level}</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {listItem.avatar && (
                                                    <Box component="img" src={listItem.avatar} sx={{ height: "20px", width: "20px", borderRadius: '50%' }} />
                                                )}
                                                <Typography level="body-xs">
                                                    {listItem.nickName ? `${listItem.userName} (${listItem.nickName})` : listItem.userName}
                                                </Typography>
                                            </Stack>
                                            <Typography level="body-xs">Прокси: {listItem.proxyAddress}</Typography>
                                            <Typography level="body-xs">Режим работы: {BotModeDescription[listItem.mode]}</Typography>
                                            {!listItem.comment ? <></> : <Typography level="body-xs">Комментарий: {listItem.comment}</Typography>}
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                <Dropdown>
                                                    <MenuButton
                                                        slots={{root: IconButton}}
                                                        slotProps={{
                                                            root: {
                                                                variant: 'plain',
                                                                color: 'neutral',
                                                                size: 'sm'
                                                            }
                                                        }}
                                                    >
                                                        <MoreHorizRoundedIcon/>
                                                    </MenuButton>
                                                    <Menu size="sm" sx={{minWidth: 140}}>
                                                        <MenuItem
                                                            onClick={() => edit(listItem.id)}>Редактировать</MenuItem>
                                                        <Divider/>
                                                        <MenuItem color="danger"
                                                                  onClick={() => remove(listItem.id)}>Удалить</MenuItem>
                                                        {!statesLoaded
                                                        ?<></>
                                                        :<>
                                                            <Divider/>
                                                            {state?.isStarted
                                                            ?<>
                                                                <MenuItem color="danger" onClick={() => stop(listItem.id)}>
                                                                    <ListItemIcon sx={{ minWidth: 0 }}><StopIcon fontSize="small" /></ListItemIcon>
                                                                    <ListItemText primary="Остановить"
                                                                                primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                                </MenuItem>
                                                                <Divider/>
                                                                <MenuItem color="warning" onClick={() => restart(listItem.id)}>
                                                                    <ListItemIcon sx={{ minWidth: 0 }}><RestartAltIcon fontSize="small" /></ListItemIcon>
                                                                    <ListItemText primary="Перезапустить"
                                                                                primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                                </MenuItem>
                                                            </>
                                                            : <MenuItem color="success" onClick={() => start(listItem.id)}>
                                                                <ListItemIcon sx={{ minWidth: 0 }}><PlayArrowIcon fontSize="small" /></ListItemIcon>
                                                                <ListItemText primary="Запустить"
                                                                            primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                            </MenuItem>}
                                                        </>}
                                                    </Menu>
                                                </Dropdown>
                                            </Box>
                                        </div>
                                    </ListItemContent>
                                    <Chip
                                        variant="soft"
                                        size="sm"
                                        startDecorator={listItem.isDisabled ? <BlockIcon/> : <CheckRoundedIcon/>}
                                        color={listItem.isDisabled ? 'danger' : 'success'}
                                    >
                                        {listItem.isDisabled ? "Выключен" : "Активен"}
                                    </Chip>
                                    {!state
                                        ?<>-</>
                                        :<Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title={state.stopReason}>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={getStateIcon(state)}
                                                    color={getStateColor(state)}
                                                >
                                                    {getStateDescription(state)}
                                                </Chip>
                                            </Tooltip>
                                            {renderInventoryLoadHint(state)}
                                        </Stack>}
                                </ListItem>
                                <ListDivider/>
                            </List>)
                        })}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={filterRequest.page === 1}
                                onClick={() => setFilterRequest(prevRequest => ({
                                    ...prevRequest,
                                    page: prevRequest.page - 1
                                }))}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {filterRequest.page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={filterRequest.page === pageCount}
                                onClick={() => setFilterRequest(prevRequest => ({
                                    ...prevRequest,
                                    page: prevRequest.page + 1
                                }))}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </>
            }
        </Box>
    );
}